import Tabs, { TabsProps } from 'antd/lib/tabs';
import React, { RefObject, useContext, useEffect, useState } from 'react';
import { useCompanies } from '../../../../components/selectors/GlobalCompanySelector/CompanyContext';
import { SmartSiteSanSelect } from '../../../../components/sidebar-filters/site-san-select/SmartSiteSanSelect';
import {
    EnergyUsageSidebarFiltersContext,
    IEnergyUsageSidebarFiltersContext,
    useEnergyUsageSidebarFiltersContext,
} from '../context/EnergyUsageSidebarFiltersContext';

const HEIGHT = 'calc(100vh - 250px)'; // viewport height - page header - button bar height

type TSiteSelectionTab = 'search' | 'selected';

interface IEnergyUsageSidebarSitesTabsProps {
    collapsed: boolean;
}

export const EnergyUsageSidebarSitesTabs: React.FC<IEnergyUsageSidebarSitesTabsProps> = ({ collapsed }) => {
    const [siteSelectionTab, setSiteSelectionTab] = useState<TSiteSelectionTab>('search');
    const { companyId } = useCompanies()!;

    const {
        siteFiltersCount,
        handleSiteSelectChange,
        selectedSiteSans,
        isSitesLoading,
        siteSelectOptions,
        selectedSiteOptions,
        isSelectedSiteOptionsLoading,
        handleSiteSelectClear,
        handleSiteSelectAll,
    } = useEnergyUsageSidebarFiltersContext();

    const divRef = React.useRef<HTMLDivElement>(null);
    const elementHeight = useElementHeight(divRef, [siteSelectionTab, collapsed]);
    const containerHeight = Math.max(elementHeight, 300);

    if (collapsed) return null;

    const tabs: TabsProps['items'] = [
        {
            key: 'search',
            label: 'Search',
            children: (
                <div style={{ padding: '8px' }}>
                    <SmartSiteSanSelect
                        showClear
                        showResultsCount
                        showSelectAllOption
                        disabled={siteFiltersCount === 0 && !companyId}
                        value={selectedSiteSans}
                        options={siteSelectOptions}
                        onChange={handleSiteSelectChange}
                        onClearAll={handleSiteSelectClear}
                        onSelectAll={handleSiteSelectAll}
                        height={containerHeight}
                        loading={isSitesLoading}
                    />
                </div>
            ),
        },
        {
            key: 'selected',
            label: <SelectedSitesLabel />,
            children: (
                <div style={{ padding: '8px' }}>
                    <SmartSiteSanSelect
                        showClear
                        showResultsCount={false}
                        showSelectAllOption={false}
                        loading={isSelectedSiteOptionsLoading}
                        value={selectedSiteSans}
                        options={selectedSiteOptions}
                        onChange={handleSiteSelectChange}
                        onClearAll={handleSiteSelectClear}
                        height={containerHeight}
                    />
                </div>
            ),
        },
    ];

    return (
        // TODO: investigate if need to fix height calculation to avoid overflow issues
        <div ref={divRef} style={{ height: HEIGHT }}>
            <Tabs
                defaultActiveKey="search"
                activeKey={siteSelectionTab}
                hidden={collapsed}
                destroyOnHidden={false}
                onChange={key => setSiteSelectionTab(key as TSiteSelectionTab)}
                items={tabs}
                tabBarStyle={{ marginBottom: 0, padding: '0px 8px' }}
            />
        </div>
    );
};

const SelectedSitesLabel: React.FC = () => {
    const { selectedSiteOptions } = useContext<IEnergyUsageSidebarFiltersContext>(EnergyUsageSidebarFiltersContext);

    return <span>Selected {selectedSiteOptions.length > 0 ? `(${selectedSiteOptions.length})` : ''}</span>;
};

export const useElementHeight = (ref: RefObject<HTMLElement>, dependencies: any[] = []): number => {
    const [height, setHeight] = useState<number>(0);

    useEffect(() => {
        if (!ref.current) return;

        const updateHeight = () => {
            if (ref.current) {
                setHeight(ref.current.clientHeight);
            }
        };

        // Initial measurement
        setTimeout(updateHeight, 10); // Delay to ensure DOM is ready

        // Set up resize observer to track size changes
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(ref.current);

        // Clean up observer on unmount
        return () => {
            resizeObserver.disconnect();
        };
    }, [ref, ...dependencies]);

    return height;
};
